import clsx from "clsx";
import React from "react";
import { ITemplateVersion } from "../utils/Types";
import { NavLink, useParams, useSearchParams } from "react-router-dom";
import { CalendarIcon, ClockIcon } from "@heroicons/react/24/outline";
import { FileText } from "@phosphor-icons/react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsAdmin } from "@/hooks/useIsAdmin";
interface TemplateProps {
  template: ITemplateVersion;
  icon: React.ReactNode;
  category?: string;
}

const TemplateVersion: React.FC<TemplateProps> = ({
  template,
  icon,
  category,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedVersion = searchParams.get("version");

  const { templateId = "" } = useParams<{
    templateId: string;
  }>();

  const isAdmin = useIsAdmin();

  const itemClass = clsx(
    "bg-gray-25/10 dark:bg-gray-950 shadow-md rounded-lg border-[0.5px] border-gray-25 dark:border-gray-500 hover:bg-gray-25/50 dark:hover:bg-gray-900/50 ",
    {
      "bg-sky-25/50 hover:border-sky-100 dark:bg-sky-600/50 border-sky-200 dark:border-sky-200 hover:bg-sky-25 dark:hover:bg-sky-600/80":
        template?.version == selectedVersion,
    }
  );

  const content = (
    <div className={clsx("flex flex-col p-3 max-h-[112px]", {})}>
      <div className="flex items-center justify-between">
        <div
          className={
            "flex items-center text-sm font-medium mr-2 truncate text-sky-200 dark:text-sky-50 " +
            (template?.version == selectedVersion
              ? "text-sky-200 dark:text-white"
              : "")
          }
        >
          <Tooltip>
            <TooltipContent side="top" className="bg-gray-600 text-white">
              <p>{category}</p>
            </TooltipContent>
            <TooltipTrigger asChild>
              <div className="w-fit mr-2">{icon || <FileText size={20} />}</div>
            </TooltipTrigger>
          </Tooltip>
          Version {template?.version}
        </div>

        <div className="flex items-center">
          {template?.published && (
            <div
              className={
                "flex items-center justify-center truncate h-[24px] w-fit px-2 min-w-[73px] border-[1px] border-green-300 dark:border-green-600 rounded-full bg-green-50 dark:bg-[#37415173] text-green-700 dark:text-green-400 font-medium text-sm"
              }
            >
              Active
            </div>
          )}
        </div>
      </div>
      <div className="flex">
        {((template?.default && template?.updated) ||
          (!template?.default && template?.created_at)) && (
          <>
            <div className="flex items-center text-gray-400 dark:text-gray-50 mt-2 text-xs">
              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400 dark:text-gray-50" />
              {new Date(
                template?.updated ? template?.updated_at : template?.created_at
              ).toDateString()}
            </div>
            <div className="flex items-center text-gray-400 dark:text-gray-50 mt-2 ml-2 text-xs">
              <ClockIcon className="h-4 w-4 mr-1 text-gray-400 dark:text-gray-50" />
              {new Date(
                template?.updated ? template?.updated_at : template?.created_at
              ).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return (
    <li className={itemClass}>
      {
        <NavLink
          to={`${isAdmin ? "/admin" : ""}/dashboard/templates/${
            template?.template?.id
          }?version=${template?.version}`}
          className={({ isActive }) =>
            clsx("relative focus:outline-slate-500", {
              "text-slate-400": isActive,
              "block text-slate-800": !isActive,
            })
          }
        >
          {content}
        </NavLink>
      }
    </li>
  );
};

export default TemplateVersion;
